import { metricNameToMetricId, normalizeMetricGoals } from "./metricGoals"

/** API typeId → reach label (Organic / Paid / Mixed). */
export const CLIENT_METRIC_GOAL_REACH_BY_TYPE_ID = {
  1: "Organic",
  2: "Paid",
  3: "Mixed",
}

/** API periodId → period key used in goals UI. */
export const CLIENT_METRIC_GOAL_PERIOD_BY_PERIOD_ID = {
  1: "monthly",
  2: "quarterly",
  3: "annual",
}

export const CLIENT_METRIC_GOAL_PERIOD_LABEL_BY_ID = {
  1: "Monthly",
  2: "Quarterly",
  3: "Annual",
}

/** UI reach label → API typeId. */
export const CLIENT_METRIC_GOAL_TYPE_ID_BY_REACH = {
  Organic: 1,
  Paid: 2,
  Mixed: 3,
}

/** UI period key → API periodId. */
export const CLIENT_METRIC_GOAL_PERIOD_ID_BY_KEY = {
  monthly: 1,
  quarterly: 2,
  annual: 3,
  yearly: 3,
}

export function reachLabelFromTypeId(typeId) {
  const id = Number(typeId)
  return CLIENT_METRIC_GOAL_REACH_BY_TYPE_ID[id] ?? "Organic"
}

export function periodKeyFromPeriodId(periodId) {
  const id = Number(periodId)
  return CLIENT_METRIC_GOAL_PERIOD_BY_PERIOD_ID[id] ?? "monthly"
}

export function periodLabelFromPeriodId(periodId) {
  const id = Number(periodId)
  return CLIENT_METRIC_GOAL_PERIOD_LABEL_BY_ID[id] ?? "Monthly"
}

export function typeIdFromReachLabel(reach) {
  const key = String(reach ?? "").trim()
  return CLIENT_METRIC_GOAL_TYPE_ID_BY_REACH[key] ?? 1
}

export function periodIdFromUiPeriod(period) {
  const key = uiPeriodFromClientPeriodKey(period)
  return CLIENT_METRIC_GOAL_PERIOD_ID_BY_KEY[key] ?? 1
}

/** Derive API periodValue from UI period + period start (YYYY-MM-DD). */
export function periodValueForApi(period, periodStartISO, existingPeriodValue) {
  const existing = String(existingPeriodValue ?? "").trim()
  if (existing.toLowerCase() === "all") return "All"
  const normalized = uiPeriodFromClientPeriodKey(period)
  if (!periodStartISO) return existing
  const parts = String(periodStartISO).split("-").map((p) => Number(p))
  const y = parts[0]
  const m = parts[1]
  if (!Number.isFinite(y)) return existing
  if (normalized === "annual") return String(y)
  if (normalized === "monthly" && Number.isFinite(m)) {
    return `${y}-${String(m).padStart(2, "0")}`
  }
  if (normalized === "quarterly" && Number.isFinite(m)) {
    const q = Math.floor((m - 1) / 3) + 1
    return `${y}-Q${q}`
  }
  return existing
}

/** Body for POST / PATCH client metric goals (periodValue is not sent to API). */
export function buildClientMetricGoalWriteBody({
  metricGoalId,
  goal,
  reach,
  period,
  enabled = true,
}) {
  const metricGoalApiId = Number(metricGoalId)
  if (!Number.isFinite(metricGoalApiId)) return null
  const periodKey = uiPeriodFromClientPeriodKey(period)
  const periodId = periodIdFromUiPeriod(periodKey)
  const typeId = typeIdFromReachLabel(reach)
  return {
    metricGoalId: metricGoalApiId,
    goal: Number(goal) || 0,
    periodId,
    typeId,
    enabled,
  }
}

function sameNumber(a, b) {
  return Number(a) === Number(b)
}

/**
 * True when an existing API row already matches the next Save payload,
 * so Save changes can skip that metric.
 */
export function isClientMetricGoalUnchanged(
  existing,
  { goal, reach, period, enabled = true, periodValue } = {},
) {
  if (!existing?.id) return false
  const next = buildClientMetricGoalWriteBody({
    metricGoalId: existing.metricGoalId,
    goal,
    reach,
    period,
    enabled,
  })
  if (!next) return false

  const existingEnabled = existing.enabled !== false
  const nextEnabled = next.enabled !== false
  if (!sameNumber(existing.goal, next.goal)) return false
  if (!sameNumber(existing.periodId, next.periodId)) return false
  if (!sameNumber(existing.typeId, next.typeId)) return false
  if (existingEnabled !== nextEnabled) return false

  const existingPv = String(existing.periodValue ?? "").trim()
  const nextPv = String(periodValue ?? "").trim()
  if (existingPv && nextPv && existingPv !== nextPv) return false
  return true
}

/** POST when user activates a metric via checkbox + Save & Activate (no row edit). */
export function buildClientMetricGoalActivateBody(metric, { reach } = {}) {
  const metricGoalApiId = Number(metric?.apiId)
  if (!Number.isFinite(metricGoalApiId)) return null
  const reachLabel = reach ?? metric?.source ?? "Organic"
  return {
    metricGoalId: metricGoalApiId,
    goal: Number(metric?.defaultTarget) || 0,
    periodId: 1,
    typeId: typeIdFromReachLabel(reachLabel),
    enabled: true,
  }
}

function normalizeEmbeddedMetricGoal(metricGoal) {
  if (!metricGoal) return null
  const rows = normalizeMetricGoals([metricGoal])
  return rows[0] ?? null
}

/** GET /client-metric-goals/clients/{clientId} */
export function normalizeClientMetricGoals(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : data?.data && typeof data.data === "object" && data.data.id != null
        ? [data.data]
        : data && typeof data === "object" && data.id != null
          ? [data]
          : []

  return list
    .filter((item) => item && item.id != null)
    .map((item) => {
      const metric = normalizeEmbeddedMetricGoal(item.metricGoal)
      const typeId = item.typeId != null ? Number(item.typeId) : null
      const periodId = item.periodId != null ? Number(item.periodId) : null
      return {
        id: Number(item.id),
        clientId: Number(item.clientId),
        enabled: item.enabled ?? null,
        goal: Number(item.goal) || 0,
        typeId,
        reach: reachLabelFromTypeId(typeId),
        periodId,
        periodKey: periodKeyFromPeriodId(periodId),
        periodLabel: periodLabelFromPeriodId(periodId),
        periodValue: String(item.periodValue ?? "").trim(),
        metricGoalId: metric?.apiId ?? item.metricGoal?.id ?? item.metricGoalId ?? null,
        metricId: metric
          ? metricNameToMetricId(metric.metricName)
          : metricNameToMetricId(item.metricGoal?.metricName ?? item.metricName ?? ""),
        metricGoal: metric,
      }
    })
    .filter((row) => Number.isFinite(row.id))
}

export function isClientMetricGoalRowEnabled(row) {
  if (!row) return false
  return row.enabled !== false
}

/** Checkbox selection + field values for a goal type from GET client-metric-goals. */
export function buildGoalTypeDraftFromClientApi(rows, goalTypeId, goalMetricLookup = {}) {
  const draft = {
    metrics: new Set(),
    targets: {},
    periods: {},
    reach: {},
    periodStarts: {},
    periodValues: {},
  }
  const typeId = Number(goalTypeId)
  if (!Number.isFinite(typeId) || !Array.isArray(rows)) return draft

  rows.forEach((row) => {
    if (Number(row.metricGoal?.metricGoalTypeId) !== typeId) return
    const metricId = row.metricId
    if (!metricId) return
    const metric = goalMetricLookup[metricId]
    if (metric) {
      applyClientRowToMetricDraft(metricId, row, metric, draft)
    }
    if (isClientMetricGoalRowEnabled(row)) {
      draft.metrics.add(metricId)
    }
  })

  return draft
}

export function uiPeriodFromClientPeriodKey(periodKey) {
  const key = String(periodKey ?? "").trim().toLowerCase()
  if (key === "yearly" || key === "year") return "annual"
  if (key === "month" || key.startsWith("month")) return "monthly"
  if (key === "quarter" || key.startsWith("quarter")) return "quarterly"
  if (key === "annual" || key.startsWith("annual")) return "annual"
  if (key === "monthly" || key === "quarterly") return key
  return "monthly"
}

/** Map API periodValue + periodId to a goal period start date (YYYY-MM-DD). */
export function periodStartKeyFromClientRow(row) {
  const period = uiPeriodFromClientPeriodKey(row?.periodKey)
  const pv = String(row?.periodValue ?? "").trim()
  if (pv.toLowerCase() === "all") return null
  if (period === "annual" && /^\d{4}$/.test(pv)) return `${pv}-01-01`
  if (period === "monthly" && /^\d{4}-\d{2}$/.test(pv)) return `${pv}-01`
  if (period === "monthly" && /^\d{4}$/.test(pv)) return `${pv}-01-01`
  if (period === "quarterly" && /^\d{4}-Q([1-4])$/i.test(pv)) {
    const m = /^(\d{4})-Q([1-4])$/i.exec(pv)
    const month = (parseInt(m[2], 10) - 1) * 3 + 1
    return `${m[1]}-${String(month).padStart(2, "0")}-01`
  }
  return null
}

export function resolveClientGoalMetricId(row, goalMetricLookup = {}) {
  if (row?.metricId && (!Object.keys(goalMetricLookup).length || goalMetricLookup[row.metricId])) {
    return row.metricId
  }
  const apiId = Number(row?.metricGoalId ?? row?.metricGoal?.apiId)
  if (Number.isFinite(apiId)) {
    const match = Object.values(goalMetricLookup).find((metric) => Number(metric?.apiId) === apiId)
    if (match?.id) return match.id
  }
  return row?.metricId || ""
}

/** Build dashboard goals state from GET client-metric-goals rows. */
export function buildGoalsUiStateFromClientRows(rows, goalMetricLookup = {}) {
  if (!Array.isArray(rows) || rows.length === 0) return null

  const metrics = new Set()
  const targets = {}
  const periods = {}
  const reach = {}
  const periodStarts = {}
  const periodValues = {}
  const goalTypeIds = new Set()

  rows.forEach((row) => {
    if (!isClientMetricGoalRowEnabled(row)) return
    const metricId = resolveClientGoalMetricId(row, goalMetricLookup)
    if (!metricId) return
    metrics.add(metricId)
    targets[metricId] = row.goal
    periods[metricId] = uiPeriodFromClientPeriodKey(row.periodKey)
    reach[metricId] = row.reach
    const startKey = periodStartKeyFromClientRow(row)
    if (startKey) periodStarts[metricId] = startKey
    if (row.periodValue) periodValues[metricId] = row.periodValue
    const typeId =
      row.metricGoal?.metricGoalTypeId ?? goalMetricLookup[metricId]?.metricGoalTypeId
    if (typeId != null) goalTypeIds.add(Number(typeId))
  })

  const goalTypeId = goalTypeIds.size === 1 ? [...goalTypeIds][0] : null

  return {
    metrics,
    targets,
    periods,
    reach,
    periodStarts,
    periodValues,
    goalTypeId,
  }
}

/** Apply saved client row values onto draft maps for a metric (setup / preset pick). */
export function applyClientRowToMetricDraft(metricId, row, metric, draft) {
  if (!metricId || !row) return
  draft.targets[metricId] = row.goal
  draft.periods[metricId] = uiPeriodFromClientPeriodKey(row.periodKey)
  draft.reach[metricId] = row.reach
  const startKey = periodStartKeyFromClientRow(row)
  if (startKey) draft.periodStarts[metricId] = startKey
  if (row.periodValue) draft.periodValues[metricId] = row.periodValue
}
